import { useMemo } from 'react';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useAors } from 'hooks/useAors';
import { useOrgEvents } from 'hooks/orgEvents/orgEventHooks';
import { IncidentListCard } from '../IncidentsTab/IncidentListCard';
import { buildIncidentListItems } from '../IncidentsTab/utils/buildIncidentListItems';
import type { IncidentListItemData } from '../IncidentsTab/IncidentListItem';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { scopeIncludesAnyAor } from './sitstat-aor-scope';

/**
 * SitStat Incidents widget — the same cards the Incidents tab lists, collapsed
 * so a rail shows the roster rather than one expanded incident. Filters and the
 * new-incident button stay behind in the tab.
 */
export function SitStatIncidentsWidget() {
  const { data: incidents = [], isLoading } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { data: orgEvents = [] } = useOrgEvents();
  const { scope } = useSitStatAorScope();

  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );

  const eventNameById = useMemo(
    () => new Map(orgEvents.map((event) => [event.id, event.name])),
    [orgEvents]
  );

  const listIncidents = useMemo<IncidentListItemData[]>(
    () =>
      buildIncidentListItems({ incidents, aorNameById, eventNameById }).filter(
        (incident) => scopeIncludesAnyAor(scope, incident.aorIds)
      ),
    [incidents, aorNameById, eventNameById, scope]
  );

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Loading incidents…
      </p>
    );
  }

  if (listIncidents.length === 0) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        {scope ? 'No active incidents in this AOR.' : 'No active incidents.'}
      </p>
    );
  }

  return (
    <div className="tw:max-h-80 tw:min-w-0 tw:space-y-2 tw:overflow-x-hidden tw:overflow-y-auto tw:pr-0.5">
      {listIncidents.map((incident) => (
        <IncidentListCard
          key={incident.id}
          incident={incident}
          defaultCollapsed
          showMapVisibilityToggle
        />
      ))}
    </div>
  );
}
